import {
  buildProjectUrlPath,
  ProjectDetailsTabPath,
} from "@repo/app-types/helpers/adminAppRoutes";
import type { FC, ReactNode } from "react";
import { Navigate, useMatch, useOutletContext, useParams } from "react-router";
import { appPaths } from "../app/appPaths.js";

/**
 * The tab content a project details page variant (recruiting vs training)
 * exposes to the tab routes through its `<Outlet context>`. Which tabs exist
 * is data-driven (the project's type), so the shared routes render whatever
 * the variant provides and redirect for the tabs it doesn't.
 */
export interface ProjectDetailsTabsContext {
  /** Training only. */
  overviewTab?: ReactNode;
  configurationTab: ReactNode;
  /** Training only; recruiting redirects to the interviews tab. */
  participantsTab?: ReactNode;
  /** Recruiting only; training redirects to the participants tab. */
  interviewsTab?: ReactNode;
  insightsTab: ReactNode;
  /** The tab unknown project URLs (including the bare one) redirect to. */
  defaultTabPath: ProjectDetailsTabPath;
}

function useProjectDetailsTabsContext(): ProjectDetailsTabsContext {
  return useOutletContext<ProjectDetailsTabsContext>();
}

export const ProjectDefaultTabRedirect: FC = () => {
  const { defaultTabPath } = useProjectDetailsTabsContext();
  const { projectId } = useParams();

  if (!projectId) {
    return <Navigate to="/" replace />;
  }

  return (
    <Navigate to={buildProjectUrlPath(projectId, defaultTabPath)} replace />
  );
};

export const ProjectOverviewTabRoute: FC = () => {
  const { overviewTab } = useProjectDetailsTabsContext();
  return overviewTab ? <>{overviewTab}</> : <ProjectDefaultTabRedirect />;
};

export const ProjectConfigurationTabRoute: FC = () => {
  const { configurationTab } = useProjectDetailsTabsContext();
  return <>{configurationTab}</>;
};

export const ProjectParticipantsTabRoute: FC = () => {
  const { participantsTab } = useProjectDetailsTabsContext();
  return participantsTab ? (
    <>{participantsTab}</>
  ) : (
    <RedirectParticipantsToInterviews />
  );
};

export const ProjectInterviewsTabRoute: FC = () => {
  const { interviewsTab } = useProjectDetailsTabsContext();
  return interviewsTab ? (
    <>{interviewsTab}</>
  ) : (
    <RedirectInterviewsToParticipants />
  );
};

export const ProjectInsightsTabRoute: FC = () => {
  const { insightsTab } = useProjectDetailsTabsContext();
  return <>{insightsTab}</>;
};

// Recruiting projects have no participants tab: forward old
// `participants[/:interviewId]` URLs to the interviews tab.
const RedirectParticipantsToInterviews: FC = () => {
  const { projectId } = useParams();
  const detailMatch = useMatch(
    `${appPaths.project.pattern}/participants/:interviewId/*`,
  );

  if (!projectId) {
    return <Navigate to="/" replace />;
  }

  return (
    <Navigate
      to={buildProjectUrlPath(
        projectId,
        ProjectDetailsTabPath.Interviews,
        detailMatch?.params.interviewId,
      )}
      replace
    />
  );
};

// Training projects have no interviews tab: forward old
// `interviews[/:participantId]` URLs to the participants tab.
const RedirectInterviewsToParticipants: FC = () => {
  const { projectId, interviewId } = useParams();

  if (!projectId) {
    return <Navigate to="/" replace />;
  }

  return (
    <Navigate
      to={buildProjectUrlPath(
        projectId,
        ProjectDetailsTabPath.Participants,
        interviewId,
      )}
      replace
    />
  );
};
